import './style.css'
import { isAddress, type Address, type Hash } from 'viem'
import { loadBoard, loadRecentBurns, wifBalance, wifPrice, type Board, type Burn, type Listing } from './chain'
import { explorerAddress, explorerTx, FLATBOARD, MIN_BOOST_BURN, MIN_LIST_BURN, REPO_URL, SITE_URL, WIF } from './config'
import { ago, dollars, duration, hostname, parseWif, shortAddress, toWif, wif } from './format'
import { decay, secondsToHalf } from './heat'
import { HIDDEN } from './hidden'
import { $, esc, safeUrl } from './html'
import { renderMap, type MapPoint } from './map'
import { boostListing, explain, listProject, updateTagline, walletOptions, type Connected, type Step } from './wallet'

// -----------------------------------------------------------------------------------------
// State
// -----------------------------------------------------------------------------------------

const state = {
  board: null as Board | null,
  burns: [] as Burn[],
  price: null as number | null,
  wallet: null as Connected | null,
  balance: null as bigint | null,
  loadError: null as string | null,
  selected: null as number | null,
  /** Chain time minus local time, so the countdowns follow the chain's clock. */
  clockOffset: 0,
  busy: false,
}

const deployed = FLATBOARD !== ''
const now = () => Math.floor(Date.now() / 1000) + state.clockOffset

const heatNow = (l: Listing) => decay(l.heat, BigInt(Math.max(0, now() - state.board!.readAt)))

function ranked(): Listing[] {
  if (!state.board) return []
  return state.board.listings
    .filter((l) => !HIDDEN.has(l.id))
    .map((l) => ({ l, h: heatNow(l) }))
    .sort((a, b) => cmp(b.h, a.h) || cmp(b.l.burned, a.l.burned) || a.l.id - b.l.id)
    .map(({ l }) => l)
}

const cmp = (x: bigint, y: bigint) => (x > y ? 1 : x < y ? -1 : 0)
const rankOf = (id: number) => ranked().findIndex((l) => l.id === id) + 1
const byId = (id: number) => state.board?.listings.find((l) => l.id === id) ?? null

// -----------------------------------------------------------------------------------------
// Rendering
// -----------------------------------------------------------------------------------------

function renderAll() {
  renderMapSection()
  renderStats()
  renderBoard()
  renderAlmanac()
  renderWalletButton()
}

function renderMapSection() {
  const points: MapPoint[] = ranked().map((l, i) => {
    const heat = toWif(heatNow(l))
    return {
      id: l.id,
      name: l.name,
      rank: i + 1,
      heat,
      burned: toWif(l.burned),
    }
  })
  $('#map').innerHTML = renderMap(points, state.selected)
  if (!arrived && points.length) {
    arrived = true
    const svg = $('#map .chart-svg')
    svg.classList.add('is-arriving')
    setTimeout(() => svg.classList.remove('is-arriving'), 1400)
  }
}
let arrived = false

function renderStats() {
  const b = state.board
  const burned = b ? b.totalBurned : 0n
  const usd = dollars(burned, state.price)
  $('#stats').innerHTML = `
    <div><dt>$WIF burned here</dt><dd>${wif(burned)}${usd ? ` <span class="fiat">${usd}</span>` : ''}</dd></div>
    <div><dt>Projects</dt><dd>${b ? ranked().length : '0'}</dd></div>
    <div><dt>Wallets burning</dt><dd>${b ? b.burners.length : '0'}</dd></div>`
}

function renderBoard() {
  const body = $('#board-body')
  if (!deployed) {
    body.innerHTML = `<div class="empty"><p>The board opens as soon as its contract is live on Robinhood Chain.</p></div>`
    return
  }
  if (state.loadError && !state.board) {
    body.innerHTML = `<div class="empty"><p>${esc(state.loadError)}</p><button class="button" type="button" data-action="retry">Try again</button></div>`
    return
  }
  if (!state.board) {
    body.innerHTML = `<p class="loading">Reading the board from Robinhood Chain</p>`
    return
  }
  const rows = ranked()
  if (rows.length === 0) {
    body.innerHTML = `<div class="empty"><p>No one has listed a project yet. The first listing takes the pole.</p>
      <button class="button button-burn" type="button" data-action="new">List a project</button></div>`
    return
  }
  const top = rows[0] ? toWif(heatNow(rows[0])) : 1
  body.innerHTML = `<ol class="rows">${rows
    .map((l, i) => {
      const heat = heatNow(l)
      const share = top > 0 ? Math.max(2, (toWif(heat) / top) * 100) : 0
      return `<li class="row${l.id === state.selected ? ' is-selected' : ''}">
        <span class="rank" aria-label="Rank">${i + 1}</span>
        <button class="row-main" type="button" data-open="${l.id}">
          <span class="row-name">${esc(l.name)}</span>
          <span class="row-tagline">${l.tagline ? esc(l.tagline) : esc(hostname(l.url))}</span>
        </button>
        <span class="row-heat">
          <span class="heat-figure"><span data-heat="${l.id}">${wif(heat)}</span> heat</span>
          <span class="heat-bar" aria-hidden="true"><span style="width:${share.toFixed(1)}%"></span></span>
        </span>
        <button class="button button-burn button-small" type="button" data-boost="${l.id}" aria-label="Boost ${esc(l.name)}">Boost</button>
      </li>`
    })
    .join('')}</ol>
    <p class="board-foot"><button class="button button-quiet" type="button" data-action="new">List a project</button><span>Listing burns from 50 WIF.</span></p>`
}

function renderAlmanac() {
  const b = state.board
  $('#burners').innerHTML =
    b && b.burners.length
      ? `<ol class="mini">${b.burners
          .slice(0, 8)
          .map(
            (x) => `<li><a href="${explorerAddress(x.account)}" target="_blank" rel="noopener noreferrer">${shortAddress(x.account)}</a>
              <span>${wif(x.burned)} WIF</span></li>`,
          )
          .join('')}</ol>`
      : `<p class="quiet">Burners show up here after the first burn.</p>`

  $('#burns').innerHTML = state.burns.length
    ? `<ul class="mini">${state.burns
        .slice(0, 8)
        .map((x) => {
          const l = byId(x.id)
          const name = l && !HIDDEN.has(l.id) ? esc(l.name) : `#${x.id}`
          const when = x.timestamp ? ago(x.timestamp, now()) : ''
          return `<li><a href="${explorerTx(x.hash)}" target="_blank" rel="noopener noreferrer">${wif(x.amount)} WIF for ${name}</a>
            <span>${when}</span></li>`
        })
        .join('')}</ul>`
    : `<p class="quiet">Every list and boost shows up here, with a link to the transaction.</p>`

  $('#colophon').innerHTML = `
    <p>Flatboard runs entirely on Robinhood Chain. ${
      deployed
        ? `Contract <a href="${explorerAddress(FLATBOARD)}#code" target="_blank" rel="noopener noreferrer">${shortAddress(FLATBOARD)}</a>, `
        : ''
    }$WIF <a href="${explorerAddress(WIF)}" target="_blank" rel="noopener noreferrer">${shortAddress(WIF)}</a>, source on <a href="${REPO_URL}" target="_blank" rel="noopener noreferrer">GitHub</a>.</p>
    <p>Burns are permanent and can't be undone. $WIF comes with no promise of value or return. Listings are written by whoever paid for them, so check a link before you trust it. Flatboard is not affiliated with Robinhood Markets, Inc.</p>`
}

function renderWalletButton() {
  const btn = $<HTMLButtonElement>('#wallet-button')
  if (!state.wallet) {
    btn.textContent = 'Connect wallet'
    return
  }
  const bal = state.balance === null ? '' : `<span class="wallet-balance">${wif(state.balance)} WIF</span>`
  btn.innerHTML = `${bal}<span>${shortAddress(state.wallet.account)}</span>`
}

/** Keep heat figures and countdowns moving between reads, without re-rendering anything else. */
function tick() {
  if (!state.board) return
  document.querySelectorAll<HTMLElement>('[data-heat]').forEach((el) => {
    const l = byId(Number(el.dataset.heat))
    if (l) el.textContent = wif(heatNow(l))
  })
  document.querySelectorAll<HTMLElement>('[data-half]').forEach((el) => {
    const l = byId(Number(el.dataset.half))
    if (l) el.textContent = duration(secondsToHalf(l.lastBoostAt, now()))
  })
}

// -----------------------------------------------------------------------------------------
// Listing sheet
// -----------------------------------------------------------------------------------------

const listingSheet = $<HTMLDialogElement>('#listing-sheet')

function openListing(id: number) {
  const l = byId(id)
  if (!l || HIDDEN.has(id)) {
    toast('That listing is not on the board.', 'error')
    location.hash = '#/'
    return
  }
  state.selected = id
  renderListing(l)
  if (!listingSheet.open) listingSheet.showModal()
  renderMapSection()
  renderBoard()
}

function renderListing(l: Listing) {
  const rank = rankOf(l.id)
  const url = safeUrl(l.url)
  const isLister = state.wallet?.account.toLowerCase() === l.lister.toLowerCase()
  const shareText = `${l.name} is #${rank} on Flatboard, the front page of Robinhood Chain ranked by burned $WIF.`
  const share = `https://x.com/intent/post?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(`${SITE_URL}#/l/${l.id}`)}`

  listingSheet.innerHTML = `
    <form method="dialog" class="sheet-close"><button class="icon-button" aria-label="Close">×</button></form>
    <p class="sheet-rank">Number ${rank} on the board</p>
    <h2 id="listing-name">${esc(l.name)}</h2>
    ${l.tagline ? `<p class="sheet-tagline" id="tagline-text">${esc(l.tagline)}</p>` : ''}
    ${
      url
        ? `<a class="outbound" href="${esc(url)}" target="_blank" rel="noopener noreferrer nofollow ugc">Visit ${esc(hostname(url))}</a>`
        : ''
    }

    <dl class="facts">
      <div><dt>Heat now</dt><dd><span data-heat="${l.id}">${wif(heatNow(l))}</span></dd></div>
      <div><dt>Halves again in</dt><dd><span data-half="${l.id}">${duration(secondsToHalf(l.lastBoostAt, now()))}</span></dd></div>
      <div><dt>Burned all-time</dt><dd>${wif(l.burned)} WIF</dd></div>
      <div><dt>Burns</dt><dd>${l.boosts}</dd></div>
    </dl>

    <form class="boost-form" id="boost-form" novalidate>
      <label for="boost-amount">Burn $WIF to boost</label>
      <div class="amount-row">
        <input id="boost-amount" name="amount" inputmode="decimal" autocomplete="off" value="100" aria-describedby="boost-help" />
        <span class="unit">WIF</span>
      </div>
      <div class="presets" role="group" aria-label="Quick amounts">
        ${[10, 100, 1000, 10000].map((n) => `<button type="button" class="chip" data-amount="${n}">${n.toLocaleString('en-US')}</button>`).join('')}
      </div>
      <p class="help" id="boost-help"></p>
      <button class="button button-burn button-wide" type="submit" id="boost-submit">Burn and boost</button>
    </form>

    <p class="provenance">Listed by <a href="${explorerAddress(l.lister)}" target="_blank" rel="noopener noreferrer">${shortAddress(l.lister)}</a> ${ago(l.createdAt, now())}${
      l.token ? `, token <a href="${explorerAddress(l.token)}" target="_blank" rel="noopener noreferrer">${shortAddress(l.token)}</a>` : ''
    }.</p>
    <div class="sheet-actions">
      <a class="button button-quiet" href="${share}" target="_blank" rel="noopener noreferrer">Share on X</a>
      ${isLister ? `<button class="button button-quiet" type="button" data-action="edit-tagline">Edit tagline</button>` : ''}
    </div>`

  const form = $<HTMLFormElement>('#boost-form', listingSheet)
  const input = $<HTMLInputElement>('#boost-amount', listingSheet)
  const help = $('#boost-help', listingSheet)
  const submit = $<HTMLButtonElement>('#boost-submit', listingSheet)

  const update = () => {
    const amount = parseWif(input.value)
    const usd = amount ? dollars(amount, state.price) : null
    if (amount === null) {
      help.textContent = 'Enter an amount, like 100.'
      submit.disabled = true
    } else if (amount < MIN_BOOST_BURN) {
      help.textContent = 'A boost burns at least 10 WIF.'
      submit.disabled = true
    } else if (state.balance !== null && amount > state.balance) {
      help.textContent = `Your wallet has ${wif(state.balance)} WIF.`
      submit.disabled = true
    } else {
      const after = heatNow(l) + amount
      help.textContent = `Heat goes to ${wif(after)}${usd ? `. You burn about ${usd}` : ''}.`
      submit.disabled = state.busy
    }
    submit.textContent = state.wallet ? 'Burn and boost' : 'Connect wallet to boost'
    if (!state.wallet) submit.disabled = false
  }
  input.addEventListener('input', update)
  listingSheet.querySelectorAll<HTMLButtonElement>('[data-amount]').forEach((b) =>
    b.addEventListener('click', () => {
      input.value = b.dataset.amount!
      update()
    }),
  )
  update()

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    if (!state.wallet) return openWallets()
    const amount = parseWif(input.value)
    if (!amount || amount < MIN_BOOST_BURN) return
    await runWrite(submit, async (onStep) => {
      const hash = await boostListing(state.wallet!, l.id, amount, onStep)
      await refresh()
      const fresh = byId(l.id)
      toast(`Burned ${wif(amount)} WIF. ${l.name} is now number ${rankOf(l.id)}.`, 'success', hash)
      if (fresh && listingSheet.open) renderListing(fresh)
    })
  })

  listingSheet.querySelector('[data-action="edit-tagline"]')?.addEventListener('click', () => editTagline(l))
}

function editTagline(l: Listing) {
  const actions = $('.sheet-actions', listingSheet)
  actions.outerHTML = `
    <form class="tagline-form" id="tagline-form" novalidate>
      <label for="tagline-input">New tagline</label>
      <input id="tagline-input" maxlength="96" value="${esc(l.tagline)}" />
      <p class="help" id="tagline-help">Up to 96 characters. Costs only gas, nothing is burned.</p>
      <button class="button" type="submit">Save tagline</button>
    </form>`
  const form = $<HTMLFormElement>('#tagline-form', listingSheet)
  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    const value = $<HTMLInputElement>('#tagline-input', listingSheet).value.trim()
    if (new TextEncoder().encode(value).length > 96) {
      $('#tagline-help', listingSheet).textContent = 'That is over 96 bytes. Shorten it a little.'
      return
    }
    await runWrite($<HTMLButtonElement>('button[type="submit"]', form), async () => {
      const hash = await updateTagline(state.wallet!, l.id, value)
      await refresh()
      toast('Tagline saved.', 'success', hash)
      const fresh = byId(l.id)
      if (fresh && listingSheet.open) renderListing(fresh)
    })
  })
}

listingSheet.addEventListener('close', () => {
  state.selected = null
  if (location.hash.startsWith('#/l/')) history.replaceState(null, '', '#/')
  renderMapSection()
  renderBoard()
})

// -----------------------------------------------------------------------------------------
// New listing sheet
// -----------------------------------------------------------------------------------------

const newSheet = $<HTMLDialogElement>('#new-sheet')
const bytes = (s: string) => new TextEncoder().encode(s).length

function openNew() {
  newSheet.innerHTML = `
    <form method="dialog" class="sheet-close"><button class="icon-button" aria-label="Close">×</button></form>
    <h2 id="new-title">List a project</h2>
    <p class="sheet-intro">Your listing goes on the board with heat equal to what you burn. The name and link are permanent, the tagline you can change later.</p>
    <form class="new-form" id="new-form" novalidate>
      <label for="new-name">Name</label>
      <input id="new-name" name="name" maxlength="32" autocomplete="off" required />
      <p class="help" data-for="name">Up to 32 characters.</p>

      <label for="new-url">Link</label>
      <input id="new-url" name="url" inputmode="url" placeholder="https://" autocomplete="off" required />
      <p class="help" data-for="url">Where people go when they open your listing. Starts with https://</p>

      <label for="new-tagline">Tagline <span class="optional">optional</span></label>
      <input id="new-tagline" name="tagline" maxlength="96" autocomplete="off" />
      <p class="help" data-for="tagline">One line on what it does, up to 96 characters.</p>

      <label for="new-token">Token contract <span class="optional">optional</span></label>
      <input id="new-token" name="token" placeholder="0x…" autocomplete="off" spellcheck="false" />
      <p class="help" data-for="token">If your project has its own token on Robinhood Chain.</p>

      <label for="new-amount">Burn</label>
      <div class="amount-row">
        <input id="new-amount" name="amount" inputmode="decimal" value="100" autocomplete="off" />
        <span class="unit">WIF</span>
      </div>
      <p class="help" data-for="amount">At least 50 WIF. More burn, more heat.</p>

      <button class="button button-burn button-wide" type="submit" id="new-submit">Burn and list</button>
    </form>`
  if (!newSheet.open) newSheet.showModal()

  const form = $<HTMLFormElement>('#new-form', newSheet)
  const submit = $<HTMLButtonElement>('#new-submit', newSheet)
  const field = (n: string) => (form.elements.namedItem(n) as HTMLInputElement).value.trim()
  const help = (n: string) => $(`.help[data-for="${n}"]`, newSheet)

  const validate = (show: boolean) => {
    const problems: Record<string, string | null> = {
      name:
        !field('name') ? 'Give it a name.' : bytes(field('name')) > 32 ? 'That is over 32 bytes.' : null,
      url: (() => {
        const u = field('url')
        if (!u) return 'Add a link.'
        if (!u.startsWith('https://')) return 'The link has to start with https://'
        if (/[\s"'<>\\`]/.test(u) || /[^\x21-\x7e]/.test(u)) return 'Links can’t contain spaces, quotes or non-ASCII characters.'
        if (u.length > 128) return 'Links can be up to 128 characters.'
        if (u.length <= 8 || u[8] === '/') return 'The link needs a site after https://'
        return null
      })(),
      tagline: bytes(field('tagline')) > 96 ? 'That is over 96 bytes.' : null,
      token: field('token') && !isAddress(field('token')) ? 'That is not a valid address.' : null,
      amount: (() => {
        const a = parseWif(field('amount'))
        if (a === null) return 'Enter an amount, like 100.'
        if (a < MIN_LIST_BURN) return 'Listing burns at least 50 WIF.'
        if (state.balance !== null && a > state.balance) return `Your wallet has ${wif(state.balance)} WIF.`
        return null
      })(),
    }
    const defaults: Record<string, string> = {
      name: 'Up to 32 characters.',
      url: 'Where people go when they open your listing. Starts with https://',
      tagline: 'One line on what it does, up to 96 characters.',
      token: 'If your project has its own token on Robinhood Chain.',
      amount: 'At least 50 WIF. More burn, more heat.',
    }
    let ok = true
    for (const [k, problem] of Object.entries(problems)) {
      const el = help(k)
      const input = form.elements.namedItem(k) as HTMLInputElement
      if (problem) ok = false
      if (show && problem) {
        el.textContent = problem
        el.classList.add('is-error')
        input.setAttribute('aria-invalid', 'true')
      } else {
        el.textContent = defaults[k]
        el.classList.remove('is-error')
        input.removeAttribute('aria-invalid')
      }
    }
    if (!problems.amount && show) {
      const usd = dollars(parseWif(field('amount'))!, state.price)
      if (usd) help('amount').textContent = `You burn about ${usd}. It all becomes heat.`
    }
    submit.textContent = state.wallet ? 'Burn and list' : 'Connect wallet to list'
    return ok
  }

  let touched = false
  form.addEventListener('input', () => validate(touched))
  form.addEventListener('focusout', () => {
    touched = true
    validate(true)
  })
  validate(false)

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    touched = true
    if (!validate(true)) {
      ;(form.querySelector('[aria-invalid="true"]') as HTMLElement | null)?.focus()
      return
    }
    if (!state.wallet) return openWallets()
    const token = field('token') ? (field('token') as Address) : null
    const amount = parseWif(field('amount'))!
    await runWrite(submit, async (onStep) => {
      const { hash, id } = await listProject(
        state.wallet!,
        { name: field('name'), url: field('url'), tagline: field('tagline'), token, amount },
        onStep,
      )
      await refresh()
      newSheet.close()
      toast(`Listed. ${field('name') || 'Your project'} is number ${rankOf(id)} on the board.`, 'success', hash)
      location.hash = `#/l/${id}`
    })
  })
}

// -----------------------------------------------------------------------------------------
// Wallets
// -----------------------------------------------------------------------------------------

const walletSheet = $<HTMLDialogElement>('#wallet-sheet')
const WALLET_KEY = 'flatboard:wallet'

function openWallets() {
  if (state.wallet) {
    walletSheet.innerHTML = `
      <form method="dialog" class="sheet-close"><button class="icon-button" aria-label="Close">×</button></form>
      <h2 id="wallet-title">Your wallet</h2>
      <p class="sheet-intro"><a href="${explorerAddress(state.wallet.account)}" target="_blank" rel="noopener noreferrer">${shortAddress(state.wallet.account)}</a> holds ${
        state.balance === null ? 'some' : wif(state.balance)
      } WIF${state.wallet && state.board ? `, and has burned ${wif(state.board.burners.find((b) => b.account.toLowerCase() === state.wallet!.account.toLowerCase())?.burned ?? 0n)} WIF here` : ''}.</p>
      <button class="button button-quiet button-wide" type="button" data-action="disconnect">Disconnect</button>`
    walletSheet.querySelector('[data-action="disconnect"]')!.addEventListener('click', () => {
      state.wallet = null
      state.balance = null
      safeStore(WALLET_KEY, null)
      walletSheet.close()
      renderWalletButton()
    })
    walletSheet.showModal()
    return
  }

  const options = walletOptions()
  walletSheet.innerHTML = `
    <form method="dialog" class="sheet-close"><button class="icon-button" aria-label="Close">×</button></form>
    <h2 id="wallet-title">Connect a wallet</h2>
    ${
      options.length
        ? `<ul class="wallets">${options
            .map(
              (o) => `<li><button class="wallet-option" type="button" data-wallet="${esc(o.id)}">
                ${o.icon && o.icon.startsWith('data:image/') ? `<img src="${esc(o.icon)}" alt="" width="28" height="28" />` : '<span class="wallet-blank" aria-hidden="true"></span>'}
                ${esc(o.name)}</button></li>`,
            )
            .join('')}</ul>
          <p class="help">You'll be asked to switch to Robinhood Chain if your wallet is on another network.</p>`
        : `<p class="sheet-intro">No browser wallet found. Install MetaMask or Rabby, or open this page in your wallet app's browser.</p>
           <a class="button button-wide" href="https://metamask.app.link/dapp/${esc(SITE_URL.replace(/^https:\/\//, ''))}" rel="noopener noreferrer">Open in MetaMask</a>`
    }`
  walletSheet.querySelectorAll<HTMLButtonElement>('[data-wallet]').forEach((b) =>
    b.addEventListener('click', async () => {
      const option = options.find((o) => o.id === b.dataset.wallet)!
      b.disabled = true
      try {
        setWallet(await option.connect())
        safeStore(WALLET_KEY, option.id)
        walletSheet.close()
        toast('Wallet connected.', 'success')
      } catch (err) {
        toast(explain(err), 'error')
        b.disabled = false
      }
    }),
  )
  walletSheet.showModal()
}

function setWallet(w: Connected) {
  state.wallet = w
  w.onChange((account) => {
    if (!account) {
      state.wallet = null
      state.balance = null
      renderWalletButton()
      return
    }
    state.wallet = { ...w, account }
    void refreshBalance()
  })
  renderWalletButton()
  void refreshBalance()
  rerenderOpenSheets()
}

async function refreshBalance() {
  if (!state.wallet) return
  try {
    state.balance = await wifBalance(state.wallet.account)
  } catch {
    state.balance = null
  }
  renderWalletButton()
  rerenderOpenSheets()
}

function rerenderOpenSheets() {
  if (listingSheet.open && state.selected !== null && !state.busy) {
    const l = byId(state.selected)
    if (l) renderListing(l)
  }
  if (newSheet.open) {
    const submit = newSheet.querySelector<HTMLButtonElement>('#new-submit')
    if (submit) submit.textContent = state.wallet ? 'Burn and list' : 'Connect wallet to list'
  }
}

async function resumeWallet() {
  const id = safeRead(WALLET_KEY)
  if (!id) return
  // Give wallets a moment to announce themselves.
  await new Promise((r) => setTimeout(r, 400))
  const option = walletOptions().find((o) => o.id === id)
  if (!option) return
  try {
    const w = await option.resume()
    if (w) setWallet(w)
  } catch {
    /* stay disconnected */
  }
}

// -----------------------------------------------------------------------------------------
// Writes, toasts, storage
// -----------------------------------------------------------------------------------------

async function runWrite(button: HTMLButtonElement, work: (onStep: (s: Step) => void) => Promise<void>) {
  if (state.busy) return
  state.busy = true
  const label = button.textContent
  button.disabled = true
  const onStep = (s: Step) => {
    switch (s.kind) {
      case 'approve-sign':
        button.textContent = `Approve ${wif(s.amount)} WIF in your wallet`
        break
      case 'approve-wait':
        button.textContent = 'Waiting for the approval'
        break
      case 'burn-sign':
        button.textContent = `Confirm the burn in your wallet`
        break
      case 'burn-wait':
        button.textContent = 'Burning onchain'
        break
    }
  }
  try {
    await work(onStep)
  } catch (err) {
    toast(explain(err), 'error')
  } finally {
    state.busy = false
    if (button.isConnected) {
      button.disabled = false
      button.textContent = label
    }
    void refreshBalance()
  }
}

function toast(message: string, kind: 'success' | 'error' = 'success', hash?: Hash) {
  const el = document.createElement('div')
  el.className = `toast toast-${kind}`
  el.innerHTML = `<p>${esc(message)}</p>${
    hash ? `<a href="${explorerTx(hash)}" target="_blank" rel="noopener noreferrer">View transaction</a>` : ''
  }<button class="icon-button" aria-label="Dismiss">×</button>`
  el.querySelector('button')!.addEventListener('click', () => el.remove())
  $('#toasts').append(el)
  setTimeout(() => el.remove(), kind === 'error' ? 12_000 : 8_000)
}

function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeStore(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    /* storage is a convenience only */
  }
}

// -----------------------------------------------------------------------------------------
// Data
// -----------------------------------------------------------------------------------------

async function refresh() {
  if (!deployed) return
  try {
    const board = await loadBoard()
    state.board = board
    state.clockOffset = board.readAt - Math.floor(Date.now() / 1000)
    state.loadError = null
  } catch {
    state.loadError = 'The board could not be read from Robinhood Chain. Check your connection and try again.'
  }
  renderAll()
  try {
    state.burns = await loadRecentBurns()
    renderAlmanac()
  } catch {
    /* the feed is secondary; keep what we had */
  }
}

// -----------------------------------------------------------------------------------------
// Wiring
// -----------------------------------------------------------------------------------------

document.addEventListener('click', (e) => {
  const target = e.target as HTMLElement
  const open = target.closest<HTMLElement>('[data-open]')
  const boost = target.closest<HTMLElement>('[data-boost]')
  const marker = target.closest<SVGGElement>('.marker')
  const action = target.closest<HTMLElement>('[data-action]')?.dataset.action
  if (open) location.hash = `#/l/${open.dataset.open}`
  else if (boost) {
    location.hash = `#/l/${boost.dataset.boost}`
    setTimeout(() => $<HTMLInputElement>('#boost-amount', listingSheet)?.focus(), 50)
  } else if (marker) location.hash = `#/l/${marker.dataset.id}`
  else if (action === 'new') openNew()
  else if (action === 'retry') void refresh()
})

document.addEventListener('keydown', (e) => {
  const marker = (e.target as HTMLElement).closest?.('.marker') as SVGGElement | null
  if (marker && (e.key === 'Enter' || e.key === ' ')) {
    e.preventDefault()
    location.hash = `#/l/${marker.dataset.id}`
  }
})

// Clicking the backdrop closes a sheet.
for (const sheet of [listingSheet, newSheet, walletSheet]) {
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) sheet.close()
  })
}

$('#list-open').addEventListener('click', openNew)
$('#wallet-button').addEventListener('click', openWallets)

function route() {
  const match = location.hash.match(/^#\/l\/(\d+)$/)
  if (match && state.board) openListing(Number(match[1]))
  else if (!match && listingSheet.open) listingSheet.close()
}
window.addEventListener('hashchange', route)

async function start() {
  renderAll()
  void wifPrice().then((p) => {
    state.price = p
    renderStats()
  })
  void resumeWallet()
  await refresh()
  route()
  setInterval(tick, 1000)
  setInterval(() => {
    if (!state.busy && document.visibilityState === 'visible') void refresh()
  }, 30_000)
  setInterval(() => {
    if (document.visibilityState === 'visible' && !listingSheet.open) {
      renderMapSection()
      renderBoard()
    }
  }, 15_000)
}

void start()
